import React, { useState, useEffect } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SendIcon from "@mui/icons-material/Send";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Styles from "./NotifyEmailModal.module.css";
import { ErrorMessage } from "../Toast/ErrorMessage";

// recipients: [{ type: 'COMPANY'|'CONSUMER'|'GENERATOR'|'DISCOM', id, label, group }]
const NotifyEmailModal = ({ open, onClose, onSend, companyName, recipients = [], loading }) => {
  const [recipientKey, setRecipientKey] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      setRecipientKey(recipients.length > 0 ? `${recipients[0].type}__${recipients[0].id}` : "");
      setSubject("");
      setMessage("");
    }
  }, [open]); // eslint-disable-line

  const handleSend = () => {
    if (!recipientKey) {
      ErrorMessage("Please select a recipient");
      return;
    }
    if (!subject.trim()) {
      ErrorMessage("Please enter an email subject");
      return;
    }
    if (!message.trim()) {
      ErrorMessage("Please enter a message");
      return;
    }
    const [type, id] = recipientKey.split("__");
    onSend({ recipientType: type, recipientId: id, emailSubject: subject.trim(), emailMessage: message.trim() });
  };

  const handleClose = () => {
    setSubject("");
    setMessage("");
    onClose();
  };

  // group recipients by their group label
  const groups = recipients.reduce((acc, r) => {
    if (!acc[r.group]) acc[r.group] = [];
    acc[r.group].push(r);
    return acc;
  }, {});

  const selectedRecipient = recipients.find(
    (r) => `${r.type}__${r.id}` === recipientKey
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <div className={`popupModal ${Styles.modal}`}>

        {/* Header */}
        <div className={Styles.mhead}>
          <div className={Styles.mheadLeft}>
            <div className={Styles.iconWrap}>
              <EmailOutlinedIcon style={{ fontSize: "18px", color: "#4267AF" }} />
            </div>
            <div>
              <div className={Styles.mheadTitle}>Notify via Email</div>
              {companyName && (
                <div className={Styles.mheadSub}>{companyName}</div>
              )}
            </div>
          </div>
          <button className={Styles.closeBtn} onClick={handleClose} aria-label="Close">
            <CloseIcon style={{ fontSize: "15px" }} />
          </button>
        </div>

        {/* Body */}
        <div className={Styles.mbody}>

          {/* Recipient selector */}
          <div className={Styles.field}>
            <label className={Styles.fieldLabel}>
              To <span className={Styles.req}>*</span>
            </label>
            <div className={Styles.selectWrap}>
              <select
                className={Styles.select}
                value={recipientKey}
                onChange={(e) => setRecipientKey(e.target.value)}
              >
                {recipients.length === 0 && (
                  <option value="">No recipients available</option>
                )}
                {Object.entries(groups).map(([group, items]) => (
                  <optgroup key={group} label={group}>
                    {items.map((r) => (
                      <option key={`${r.type}__${r.id}`} value={`${r.type}__${r.id}`}>
                        {r.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <KeyboardArrowDownIcon className={Styles.selectArrow} />
              {selectedRecipient && (
                <span className={`${Styles.recipientBadge} ${Styles[`badge${selectedRecipient.type}`]}`}>
                  {selectedRecipient.type}
                </span>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className={Styles.field}>
            <label className={Styles.fieldLabel}>
              Subject <span className={Styles.req}>*</span>
            </label>
            <input
              type="text"
              className={Styles.fieldInput}
              placeholder="Enter email subject…"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>

          {/* Message */}
          <div className={Styles.field}>
            <label className={Styles.fieldLabel}>
              Message <span className={Styles.req}>*</span>
            </label>
            <textarea
              className={Styles.messageArea}
              rows={6}
              placeholder="Write your message…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>

        </div>

        {/* Footer */}
        <div className={Styles.mfoot}>
          <button className={Styles.cancelBtn} onClick={handleClose} disabled={loading}>
            Cancel
          </button>
          <button
            className={Styles.sendBtn}
            onClick={handleSend}
            disabled={loading || !recipientKey || !subject.trim() || !message.trim()}
          >
            <SendIcon style={{ fontSize: "13px" }} />
            {loading ? "Sending…" : "Send Email"}
          </button>
        </div>

      </div>
    </Modal>
  );
};

export default NotifyEmailModal;
